/** @typedef {import('./types').CrmContactsQuery} CrmContactsQuery */

export const CONTACT_PAGE_SIZE = 25

/** Keys that narrow the result set (everything except paging and sorting). */
export const FILTER_KEYS = ['Search', 'Type', 'OwnerUserId', 'Mine', 'Stage', 'Industry', 'Tag', 'Stale', 'AccountId']

/** Built-in views stay client side. */
export const BUILT_IN_VIEWS = [
    { key: 'all', name: 'All', filters: {} },
    { key: 'mine', name: 'My contacts', filters: { Mine: true } },
    { key: 'prospects', name: 'Prospects', filters: { Type: 'Prospect' } },
    { key: 'customers', name: 'Customers', filters: { Type: 'Customer' } },
    { key: 'inactive', name: 'Inactive', filters: { Type: 'Inactive' } },
    { key: 'stale', name: 'Not contacted 30+ days', filters: { Stale: true } },
]

/** @type {CrmContactsQuery} */
export const DEFAULT_SORT = { Sort: 'name', Dir: 'asc' }

const isSet = value => value !== undefined && value !== null && value !== '' && value !== false

/**
 * Only the filter keys that carry a value.
 * @param {CrmContactsQuery} query
 * @returns {CrmContactsQuery}
 */
export const filtersOf = query => {
    /** @type {Record<string, unknown>} */
    const out = {}
    FILTER_KEYS.forEach(key => {
        if (isSet(query?.[key])) out[key] = query[key]
    })
    return out
}

/**
 * Query for a view: its filters, back on page 1, keeping the caller's page size and sort unless the
 * view carries its own.
 * @param {CrmContactsQuery} filters
 * @param {CrmContactsQuery} [current]
 * @returns {CrmContactsQuery}
 */
export const queryForView = (filters, current = {}) => ({
    ...filtersOf(filters),
    Page: 1,
    PageSize: current.PageSize || CONTACT_PAGE_SIZE,
    Sort: filters?.Sort || current.Sort || DEFAULT_SORT.Sort,
    Dir: filters?.Dir || current.Dir || DEFAULT_SORT.Dir,
})

/** Same set of filters, regardless of order or paging. */
export const sameFilters = (a, b) => {
    const fa = filtersOf(a)
    const fb = filtersOf(b)
    const ka = Object.keys(fa)
    if (ka.length !== Object.keys(fb).length) return false
    return ka.every(key => String(fa[key]) === String(fb[key]))
}

/** Filters set in the Filters panel / views -- the search box has its own input, so it is not counted. */
export const activeFilterCount = query => Object.keys(filtersOf(query)).filter(key => key !== 'Search').length

/**
 * Next sort state when a column header is clicked: same column flips direction, a new one starts ascending.
 * @param {CrmContactsQuery} query
 * @param {'name'|'company'|'last'|'stage'|'next'} column
 * @returns {CrmContactsQuery}
 */
export const nextSort = (query, column) => ({
    ...query,
    Page: 1,
    Sort: column,
    Dir: query.Sort === column && query.Dir === 'asc' ? 'desc' : 'asc',
})

/**
 * Pages needed to fetch every row: used by Export CSV (the API caps PageSize at 200).
 * @param {number} totalCount
 * @param {number} pageSize
 */
export const pageCount = (totalCount, pageSize) => Math.max(1, Math.ceil(totalCount / pageSize))
